import React, { Component } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import "./SearchPage.css";
import HomeSearchBarAll from "./HomeSearchBarAll";
import "bootstrap-icons/font/bootstrap-icons.css";

/* ------------------------------------------------------------------
   Filter option definitions (must match backend expectations)
------------------------------------------------------------------- */
const RECOMMENDED_OPTIONS = [
  { label: "Instant Booking",  value: "Instant Booking"  },
  { label: "Request to Book",  value: "Request to Book"  },
  { label: "Attached Bath",    value: "Attached Bath"    },
  { label: "Air Conditioning", value: "Air Conditioning" },
];

const ROOM_TYPES = [
  { label: "Any",            value: "Any"          },
  { label: "Full Apartment", value: "Entire Place" }, // maps to backend "Entire Place"
  { label: "Private Room",   value: "Private Room" },
];

const REVIEW_SCORES = ["4.9", "4.7", "4.5", "4.0", "3.5"];
const PRICE_STEP = 100;

const EMPTY_FILTERS = {
  recommended: [],   // string[]   -> "f=Instant Booking,Attached Bath"
  roomType: "Any",   // string     -> "t=Private Room" / "t=Entire Place"
  priceMin: "",      // string     -> "ps"
  priceMax: "",      // string     -> "pe"
  propertyTypes: [], // string[]   -> "p=10,17,20"
  reviewScore: "",   // string     -> "r=4.7"
};

class SearchPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      travelData: [],
      loading: true,
      error: null,
      notFound: false,

      // Search bar params (from HomeSearchBarAll)
      type: "",
      address: "",
      startDate: "",
      endDate: "",
      rooms: "",
      adults: "",
      kids: "",
      category: "",

      // Property type list (from /get_category)
      categories: [],
      categoriesLoading: true,

      // Sidebar filter state
      filters: { ...EMPTY_FILTERS },
      filtersDirty: false,
      usingFilters: false,
    };
  }

  /* -----------------------------------------------------------------
     Lifecycle
  ----------------------------------------------------------------- */
  componentDidMount() {
    const params = new URLSearchParams(window.location.search);

    this.setState(
      {
        type: params.get("t") || "",
        address: params.get("a") || "",
        startDate: params.get("s") || "",
        endDate: params.get("e") || "",
        rooms: params.get("r") || "",
        adults: params.get("ad") || "",
        kids: params.get("k") || "",
        category: params.get("c") || "",
      },
      () => {
        this.fetchSearchData(false);
        this.fetchCategories();
      }
    );
  }

  /* -----------------------------------------------------------------
     Fetch property types (categories)
  ----------------------------------------------------------------- */
  fetchCategories = async () => {
    try {
      const res = await axios.get("/get_category");
      const list = res?.data?.data || [];
      this.setState({ categories: list, categoriesLoading: false });
    } catch (e) {
      console.error("Category fetch error:", e);
      this.setState({ categories: [], categoriesLoading: false });
    }
  };

  /* -----------------------------------------------------------------
     Fetch search results
       useFilters = false -> original /q endpoint
       useFilters = true  -> new      /p endpoint with filter params
  ----------------------------------------------------------------- */
  fetchSearchData = async (useFilters = false) => {
    const {
      type, address, startDate, endDate, rooms, adults, kids, category, filters,
    } = this.state;

    this.setState({ loading: true, error: null, notFound: false });

    try {
      let url;

      if (useFilters) {
        const q = new URLSearchParams();
        if (filters.recommended.length)   q.set("f", filters.recommended.join(","));
        if (filters.roomType && filters.roomType !== "Any") q.set("t", filters.roomType);
        if (filters.priceMin !== "")      q.set("ps", filters.priceMin);
        if (filters.priceMax !== "")      q.set("pe", filters.priceMax);
        if (filters.propertyTypes.length) q.set("p", filters.propertyTypes.join(","));
        if (filters.reviewScore)          q.set("r", filters.reviewScore);

        url = `/p?${q.toString()}`;
      } else {
        url = `/q?t=${type}&a=${address}&s=${startDate}&e=${endDate}` +
              `&r=${rooms}&ad=${adults}&k=${kids}&c=${category}`;
      }

      const response = await axios.get(url);
      const data = response?.data?.data || [];

      this.setState({
        travelData: data,
        loading: false,
        notFound: data.length === 0,
        usingFilters: useFilters,
      });
    } catch (e) {
      console.error("API Error:", e);

      // Backend returns 404 when nothing matches -> treat as "no results"
      if (e?.response?.status === 404) {
        this.setState({
          loading: false,
          travelData: [],
          notFound: true,
          usingFilters: useFilters,
        });
        return;
      }

      this.setState({
        loading: false,
        error: "We encountered a network issue while retrieving your results.",
      });
    }
  };

  /* -----------------------------------------------------------------
     Filter interaction handlers
  ----------------------------------------------------------------- */
  patchFilters = (patch) => {
    this.setState((prev) => ({
      filters: { ...prev.filters, ...patch },
      filtersDirty: true,
    }));
  };

  toggleRecommended = (value) => {
    const { recommended } = this.state.filters;
    const next = recommended.includes(value)
      ? recommended.filter((v) => v !== value)
      : [...recommended, value];
    this.patchFilters({ recommended: next });
  };

  togglePropertyType = (id) => {
    const { propertyTypes } = this.state.filters;
    const next = propertyTypes.includes(id)
      ? propertyTypes.filter((v) => v !== id)
      : [...propertyTypes, id];
    this.patchFilters({ propertyTypes: next });
  };

  adjustPrice = (field, delta) => {
    const current = Number(this.state.filters[field]) || 0;
    const next = Math.max(0, current + delta);
    this.patchFilters({ [field]: next === 0 ? "" : String(next) });
  };

  clearAll = () => {
    this.setState({
      filters: { ...EMPTY_FILTERS, recommended: [], propertyTypes: [] },
      filtersDirty: false,
    });
  };

  applyFilters = () => {
    if (!this.state.filtersDirty) return;
    this.fetchSearchData(true);
  };

  /* -----------------------------------------------------------------
     Card rendering
  ----------------------------------------------------------------- */
  handleNavigateToDetails = (id) => {
    this.props.navigate(`/listing/${id}`);
  };

  renderTravelCard = (data) => {
    const baseUrl = axios.defaults.baseURL
      ? axios.defaults.baseURL.replace("/api", "")
      : "";
    const uploadUrl = `${baseUrl}/uploads/posts/`;

    return (
      <div
        key={data.id}
        className="travel-card"
        onClick={() => this.handleNavigateToDetails(data.id)}
      >
        <div className="card-image-wrapper">
          <img
            src={`${uploadUrl}${data.image}`}
            alt={data.title}
            className="card-img"
            onError={(e) => {
              e.target.src =
                "https://via.placeholder.com/400x300?text=Image+Not+Available";
            }}
          />
          {data.isHighlyRecommended && (
            <span className="badge-recommended">Highly Recommended</span>
          )}
        </div>

        <div className="card-content">
          <h3 className="card-title">{data.title}</h3>

          <div className="card-footer">
            <div className="price-info">
              <p className="price-primary">
                <span className="currency">৳</span>
                {Number(data.price).toLocaleString()}
                <span className="per-day"> / day</span>
              </p>
              <p className="price-total">
                Total: ৳{Number(data.price).toLocaleString()} for{" "}
                {data.days || 1} day(s)
              </p>
            </div>

            <div className="rating-info">
              <i className="bi bi-star-fill star"></i>
              <span className="rating-value">{data.ratings || "New"}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* -----------------------------------------------------------------
     Sidebar render
  ----------------------------------------------------------------- */
  renderSidebar = () => {
    const { filters, filtersDirty, categories, categoriesLoading } = this.state;

    return (
      <aside className="filters-sidebar">
        {/* Header ---------------------------------------------------- */}
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            <i className="bi bi-sliders2"></i> Filters
          </h2>
          <button
            type="button"
            className="clear-all-link"
            onClick={this.clearAll}
            disabled={
              !filtersDirty &&
              filters.recommended.length === 0 &&
              filters.propertyTypes.length === 0 &&
              !filters.priceMin &&
              !filters.priceMax &&
              !filters.reviewScore &&
              filters.roomType === "Any"
            }
          >
            Clear All
          </button>
        </div>

        {/* 1. Recommended for you ------------------------------------ */}
        <div className="filter-section">
          <h3 className="filter-section-title">Recommended for you</h3>
          <p className="filter-hint">Choose one or more</p>
          <div className="filter-pills">
            {RECOMMENDED_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`filter-pill ${
                  filters.recommended.includes(opt.value) ? "active" : ""
                }`}
                onClick={() => this.toggleRecommended(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 2. Type of place ------------------------------------------ */}
        <div className="filter-section">
          <h3 className="filter-section-title">Type of place</h3>
          <div className="filter-pills">
            {ROOM_TYPES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`filter-pill ${
                  filters.roomType === opt.value ? "active" : ""
                }`}
                onClick={() => this.patchFilters({ roomType: opt.value })}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 3. Price range -------------------------------------------- */}
        <div className="filter-section">
          <h3 className="filter-section-title">Price range (৳)</h3>
          <div className="price-input-group">
            <div className="price-input-wrapper">
              <input
                type="number"
                min="0"
                placeholder="Min"
                value={filters.priceMin}
                onChange={(e) =>
                  this.patchFilters({ priceMin: e.target.value })
                }
              />
              <div className="price-stepper">
                <button
                  type="button"
                  onClick={() => this.adjustPrice("priceMin", PRICE_STEP)}
                  aria-label="Increase min price"
                >
                  <i className="bi bi-caret-up-fill"></i>
                </button>
                <button
                  type="button"
                  onClick={() => this.adjustPrice("priceMin", -PRICE_STEP)}
                  aria-label="Decrease min price"
                >
                  <i className="bi bi-caret-down-fill"></i>
                </button>
              </div>
            </div>

            <div className="price-input-wrapper">
              <input
                type="number"
                min="0"
                placeholder="Max"
                value={filters.priceMax}
                onChange={(e) =>
                  this.patchFilters({ priceMax: e.target.value })
                }
              />
              <div className="price-stepper">
                <button
                  type="button"
                  onClick={() => this.adjustPrice("priceMax", PRICE_STEP)}
                  aria-label="Increase max price"
                >
                  <i className="bi bi-caret-up-fill"></i>
                </button>
                <button
                  type="button"
                  onClick={() => this.adjustPrice("priceMax", -PRICE_STEP)}
                  aria-label="Decrease max price"
                >
                  <i className="bi bi-caret-down-fill"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Property Type ------------------------------------------ */}
        <div className="filter-section">
          <h3 className="filter-section-title">Property Type</h3>
          {categoriesLoading ? (
            <p className="filter-hint">Loading categories…</p>
          ) : categories.length === 0 ? (
            <p className="filter-hint">No property types available</p>
          ) : (
            <div className="property-types-list">
              {categories.map((cat) => {
                const id = String(cat.id);
                const checked = filters.propertyTypes.includes(id);
                return (
                  <label key={id} className="property-type-item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => this.togglePropertyType(id)}
                    />
                    <span className="property-type-label">
                      {cat.category_name}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* 5. Review Score ------------------------------------------- */}
        <div className="filter-section">
          <h3 className="filter-section-title">Review Score</h3>
          <div className="review-options">
            {REVIEW_SCORES.map((score) => (
              <label key={score} className="review-option">
                <input
                  type="radio"
                  name="reviewScore"
                  checked={filters.reviewScore === score}
                  onChange={() =>
                    this.patchFilters({ reviewScore: score })
                  }
                />
                <span className="review-option-content">
                  <i className="bi bi-star-fill"></i> {score}+
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* 6. Submit ------------------------------------------------- */}
        <div className="sidebar-footer">
          <button
            type="button"
            className={`show-places-btn ${filtersDirty ? "" : "is-disabled"}`}
            disabled={!filtersDirty}
            onClick={this.applyFilters}
          >
            {filtersDirty ? (
              <>
                <i className="bi bi-search"></i> Show Places
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle"></i> Filters Applied
              </>
            )}
          </button>
        </div>
      </aside>
    );
  };

  /* -----------------------------------------------------------------
     Main render
  ----------------------------------------------------------------- */
  render() {
    const { travelData, loading, address, category, error, notFound } =
      this.state;
    const searchLabel =
      address || category
        ? `${address} ${category}`.trim()
        : "your destination";

    return (
      <>
        <HomeSearchBarAll />

        <div className="search-container">
          <div className="search-layout">
            {this.renderSidebar()}

            <main className="search-results">
              {/* LOADING ------------------------------------------- */}
              {loading && (
                <div className="status-screen loading-state">
                  <div className="spinner"></div>
                  <h2>Searching properties…</h2>
                  <p>
                    Gathering the best options available in {searchLabel}.
                  </p>
                </div>
              )}

              {/* ERROR --------------------------------------------- */}
              {!loading && error && (
                <div className="not-found-container">
                  <div className="not-found-card">
                    <div className="not-found-icon">
                      <i className="bi bi-search"></i>
                      <span className="not-found-icon-badge">
                        <i className="bi bi-x-lg"></i>
                      </span>
                    </div>

                    <span className="not-found-label">NO RESULTS</span>
                    <h2>Nothing found!</h2>
                    <p>
                      We couldn't find any results matching your search. Try
                      adjusting your keywords or searching for something else.
                    </p>

                    <div className="not-found-tips">
                      <i className="bi bi-lightbulb"></i>
                      <span>
                        Try checking your spelling or using different keywords.
                      </span>
                    </div>

                    <button
                      type="button"
                      className="not-found-button"
                      onClick={() => window.location.reload()}
                    >
                      <i className="bi bi-arrow-clockwise"></i> Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* NO RESULTS ---------------------------------------- */}
              {!loading && !error && notFound && (
                <div className="not-found-container">
                  <div className="not-found-card">
                    <div className="not-found-icon">
                      <i className="bi bi-search"></i>
                      <span className="not-found-icon-badge">
                        <i className="bi bi-x-lg"></i>
                      </span>
                    </div>

                    <span className="not-found-label">NO RESULTS</span>
                    <h2>No exact matches</h2>
                    <p>
                      We couldn't find any properties in{" "}
                      <strong>{searchLabel}</strong> for your selected criteria.
                      Try adjusting your filters or dates.
                    </p>

                    <div className="not-found-tips">
                      <i className="bi bi-lightbulb"></i>
                      <span>
                        Tip: Use <strong>Clear All</strong> in the sidebar to
                        reset every filter, or widen your price range.
                      </span>
                    </div>

                    <button
                      type="button"
                      className="not-found-button"
                      onClick={this.clearAll}
                    >
                      <i className="bi bi-arrow-clockwise"></i> Reset Filters
                    </button>
                  </div>
                </div>
              )}

              {/* RESULTS ------------------------------------------- */}
              {!loading && !error && !notFound && (
                <div className="results-wrapper">
                  <header className="search-header">
                    <p className="results-count">
                      {travelData.length}{" "}
                      {travelData.length === 1 ? "Property" : "Properties"}{" "}
                      found in {searchLabel}
                    </p>
                    <h1 className="results-title">Search Results</h1>
                  </header>

                  <div className="travel-grid">
                    {travelData.map(this.renderTravelCard)}
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </>
    );
  }
}

function SearchPageWithNavigate(props) {
  const navigate = useNavigate();
  return <SearchPage {...props} navigate={navigate} />;
}

export default SearchPageWithNavigate;

